<script>
import RawData from "@/components/widgets/raw-data.vue";
import LoadingTemplate from "@/components/widgets/loading-template.vue";
import CollectionList from "@/components/widgets/rcd-collection-list.vue";

export default {
    name: "RCDList",
    components: {
        RawData,
        LoadingTemplate,
        CollectionList  
    },
    props: {
        rcdData: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        type: {
            type: String,
            default: "draft", // draft or posted
        },
    },
    data() {
        return {
            filter: "",
            currentPage: 1,
            perPage: 10,
            pageOptions: [10, 25, 50, 100],
        };
    },
    computed: {
        filtered() {
            let data = this.rcdData;
            if (this.filter && this.filter.trim() != "") {
                data = data.filter((item) => {
                    return (
                        item.rcdNumber?.toLowerCase().includes(this.filter.toLowerCase()) ||
                        item.description?.toLowerCase().includes(this.filter.toLowerCase()) ||
                        item.collectingOfficer?.toLowerCase().includes(this.filter.toLowerCase())
                    );
                });
            }
            return data;
        },
        paginatedData() {
            const startIndex = (this.currentPage - 1) * this.perPage;
            const endIndex = startIndex + this.perPage;
            return this.filtered.slice(startIndex, endIndex);
        },
        totalPages() {
            return Math.ceil(this.filtered.length / this.perPage);
        },
    },
    methods: {
        toggleExpand(item) {
            this.$set(item, 'opened', !item.opened);
        },
        getRCDTotal(rcd) {
            if (!rcd.rcdDetails || !Array.isArray(rcd.rcdDetails)) return 0;
            return rcd.rcdDetails.reduce((total, collection) => {
                return total + (collection.totalAmount || 0);
            }, 0);
        },
        editRCD(item) {
            this.$emit("edit-rcd", item);
        },
        deleteRCD(item) {
            this.$emit("delete-rcd", item);
        },
        printRCD(item) {
            this.$emit("print-rcd", item);
        },
        postRCD(item) {
            this.$emit("post-rcd", item);
        },
        getAttachments(item) {
            this.$emit("attachment", {
                att: item.attachments || [],
                id: item.id
            });
        },
        addRCD() {
            this.$emit("add-rcd");
        },
        showCollections(rcd) {
            rcd.collectionShow = !rcd.collectionShow;
        },
    },
};
</script>

<template>
    <div class="created-rcd">
        <div class="card shadow-none">
            <div class="card-body bg-soft" :class="{'bg-primary': type === 'draft', 'bg-success': type === 'posted'}">
                <div class="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <h4 class="card-title mb-0">
                            {{type.charAt(0).toUpperCase() + type.slice(1)}} RCD
                        </h4>
                        <p class="card-title-desc mb-0">
                            List of {{type}} Reports of Collections & Deposits
                        </p>
                    </div>
                    <div v-if="rcdData.length > 0">
                        <div class="d-flex">
                            <template v-if="rcdData.length > 0">
                                <input
                                    autocomplete="off"
                                    type="text"
                                    v-model="filter"
                                    placeholder="Search RCD..."
                                    class="form-control"
                                />
                                <b-form-select
                                    class="form-select ms-1 w-25"
                                    v-model="perPage"
                                    :options="pageOptions"
                                ></b-form-select>
                            </template>
                        </div>
                    </div>
                </div>

                <div v-if="loading" class="text-center my-5 py-5">
                    <LoadingTemplate class="my-5"/>
                </div>

                <div v-if="!loading && rcdData.length == 0" class="text-center">
                    <h5>No data available</h5>
                </div>

                <div v-if="!loading && rcdData.length > 0">
                    <!-- RCD List -->
                    <div class="rcd-list">
                        <div 
                            v-for="(rcd, index) in paginatedData" 
                            :key="rcd.id || index"
                            class="card border mb-1 shadow-none position-relative"
                        >
                            <RawData :data="rcd" v-if="$store.state.decrypt.isAdmin()"/>
                            <b-card-header 
                                class="cursor-pointer border-bottom-0" 
                                @click="toggleExpand(rcd)"
                            >
                                <div class="d-flex justify-content-between align-items-center">
                                    <div class="flex-grow-1">
                                        <h6 class="mb-1">
                                            <a class="text-dark font-weight-semibold">
                                                <span class="text-warning">[{{ rcd.rcdNumber || 'N/A' }}]</span>
                                                :&nbsp;
                                                {{ rcd.description || 'No Description' }}
                                                <span class="ms-1 text-orange">({{ formatCurrency(getRCDTotal(rcd)) || formatCurrency(0) }})</span>
                                            </a>
                                        </h6>
                                    </div>
                                    <div class="d-flex align-items-center">
                                        <div>
                                            <span class="d-flex align-items-center text-truncate">
                                                <small v-if="!rcd.opened">Click to expand</small>
                                                <i 
                                                    class="bx bx-chevron-right rotate font-size-18 ms-3"
                                                    :class="{'rotate-90': rcd.opened }"
                                                ></i>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </b-card-header>
                            
                            <b-collapse
                                :visible="rcd.opened"
                                :accordion="`rcd-accordion-${type}-${rcd.id}`"
                                role="tabpanel"
                            >
                                <b-card-body class="border">
                                    <div class="d-flex align-items-center justify-content-between cursor-pointer border-bottom pb-2 mb-2">
                                        <h5 class="mb-0"><i class="bx bx-receipt"></i> RCD Details</h5>
                                        <h5 class="mb-0">
                                            <i
                                                class="
                                                    bx
                                                    bx-chevron-right
                                                    rotate
                                                "
                                                :class="{
                                                    'rotate-90':
                                                        rcd.detailShow,
                                                }"
                                            ></i>
                                        </h5>
                                    </div>  
                                    <div class="row">
                                        <div class="col-md-6 border-end">
                                            <table class="w-100">
                                                <tbody>
                                                    <tr>
                                                        <th class="p-1" style="width: 40%;">RCD Number:</th>
                                                        <td class="p-1 font-weight-bold text-green">
                                                            {{ rcd.rcdNumber || '-' }}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <th class="p-1" style="width: 40%;">Description:</th>
                                                        <td class="p-1 font-weight-bold text-green">
                                                            {{ rcd.description || '-' }}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <th class="p-1" style="width: 40%;">RCD Date: </th>
                                                        <td class="p-1 font-weight-bold text-green">
                                                            {{ rcd.rcdDate ? formatDate(new Date(rcd.rcdDate)) : '-' }}
                                                        </td>
                                                    </tr>
                                                    
                                                    <tr>
                                                        <th class="p-1" style="width: 40%;">Total Collections: </th>
                                                        <td class="p-1 font-weight-bold text-green">
                                                           {{ rcd.rcdDetails ? rcd.rcdDetails.length : 0 }}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <th class="p-1" style="width: 40%;">Total Amount: </th>
                                                        <td class="p-1 font-weight-bold text-green">
                                                            {{ formatCurrency(getRCDTotal(rcd)) || formatCurrency(0) }}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                        <div class="col-md-6">
                                            <table class="w-100">
                                                <tbody>
                                                    <tr>
                                                        <th class="p-1" style="width: 40%;">Fund Source: </th>
                                                        <td class="p-1 font-weight-bold text-green">
                                                            <span class="text-white font-weight-bold">{{ rcd.fundSource || '-' }}</span>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <th class="p-1" style="width: 40%;">Deposit Bank Name: </th>
                                                        <td class="p-1 font-weight-bold text-green">
                                                            {{ rcd.depositBankName || '-' }}
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <th class="p-1" style="width: 40%;">Deposit Date: </th>
                                                        <td class="p-1 font-weight-bold text-green">
                                                            {{ rcd.depositDate ? formatDate(new Date(rcd.depositDate)) : '-' }}
                                                        </td>
                                                    </tr>
                                                     <tr>
                                                        <th class="p-1" style="width: 40%;">Deposit Reference No.: </th>
                                                        <td class="p-1 font-weight-bold text-green">
                                                            {{ rcd.depositReferenceNumber || '-' }}
                                                        </td>
                                                    </tr>
                                                     <tr>
                                                        <th class="p-1" style="width: 40%;">Deposited By: </th>
                                                        <td class="p-1 font-weight-bold text-green">
                                                            {{ rcd.depositedBy || '-' }}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                    <hr />
                                    <div 
                                        class="d-flex align-items-center justify-content-between cursor-pointer border-bottom pb-2 mb-2"
                                        @click="showCollections(rcd)"
                                    >
                                        <h5 class="mb-2"><i class="bx bx-receipt"></i> Collection List</h5>
                                        <h5 class="mb-0">
                                            <i
                                                class="
                                                    bx
                                                    bx-chevron-right
                                                    rotate
                                                "
                                                :class="{
                                                    'rotate-90':
                                                        rcd.collectionShow,
                                                }"
                                            ></i>
                                        </h5>
                                    </div> 
                                    <CollectionList 
                                        :rcd="rcd"
                                        :type="type"    
                                    />
                                    <div class="d-flex justify-content-end mt-3">
                                        <button 
                                            class="btn btn-purple me-2"
                                            @click="$emit('print', rcd)"
                                        >
                                            <i class="bx bx-receipt"></i> Print RCD
                                        </button>
                                        <template v-if="type === 'draft'">
                                            <button 
                                                class="btn btn-success me-2"
                                                @click="$emit('post', rcd)"
                                                :disabled="!rcd.rcdDetails || rcd.rcdDetails.length === 0"
                                            >
                                                <i class="bx bx-check-circle"></i> Post RCD
                                            </button>
                                            <button 
                                                class="btn btn-primary me-2"
                                                @click="$emit('update', rcd)"
                                            >
                                                <i class="bx bx-edit"></i> Edit RCD
                                            </button>
                                            <button 
                                                class="btn btn-danger me-2"
                                                @click="$emit('delete', rcd)"
                                                :disabled="!rcd.rcdDetails || rcd.rcdDetails.length === 0"
                                            >
                                                <i class="bx bx-trash"></i> Delete RCD
                                            </button>
                                        </template>
                                        <button 
                                            class="btn btn-warning me-2"
                                            @click="$emit('attachment', {att: ['rcd'], id: rcd.id})"
                                        >
                                            <i class="bx bx-paperclip"></i> Attachments
                                        </button>
                                    </div>
                                </b-card-body>
                            </b-collapse>
                        </div>
                    </div>

                    <!-- Pagination -->
                    <div v-if="totalPages > 1" class="d-flex justify-content-center mt-3">
                        <b-pagination
                            v-model="currentPage"
                            :total-rows="filtered.length"
                            :per-page="perPage"
                            aria-controls="rcd-table"
                            align="center"
                        ></b-pagination>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.rotate {
    transition: transform 0.2s ease-in-out;
}

.rotate-90 {
    transform: rotate(90deg);
}

.cursor-pointer {
    cursor: pointer;
}

.font-weight-semibold {
    font-weight: 600;
}

.text-truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.bg-soft {
    background-color: rgba(85, 110, 230, 0.1) !important;
}

.bg-info {
    background-color: rgba(23, 162, 184, 0.1) !important;
}

.bg-success {
    background-color: rgba(40, 199, 111, 0.1) !important;
}
</style>